/**
 * Promise race helper for player hot paths that must not hang forever
 * (playback-heartbeat build + POST). Native IPC / storage.estimate / fetch without
 * AbortSignal can wedge indefinitely on low-end Android WebViews.
 */

export class TimeoutError extends Error {
  readonly label: string
  readonly timeoutMs: number

  constructor(label: string, timeoutMs: number) {
    super(`${label} timed out after ${timeoutMs}ms`)
    this.name = 'TimeoutError'
    this.label = label
    this.timeoutMs = timeoutMs
    Object.setPrototypeOf(this, TimeoutError.prototype)
  }
}

/** POST /playback-heartbeat network budget (per attempt; retries add backoff). */
export const PLAYBACK_HEARTBEAT_FETCH_TIMEOUT_MS = 15_000
/** `navigator.storage.estimate()` — optional metadata on heartbeat. */
export const PLAYBACK_HEARTBEAT_STORAGE_ESTIMATE_TIMEOUT_MS = 3_000
/** Android `deviceId()` for `androidIdPrefix` on heartbeat. */
export const PLAYBACK_HEARTBEAT_DEVICE_ID_TIMEOUT_MS = 3_000
/** Tauri `getHardwareCapacity` invoke budget (timeout ends the retry loop — do not stack). */
export const PLAYBACK_HEARTBEAT_HARDWARE_CAPACITY_ATTEMPT_TIMEOUT_MS = 3_000

export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  label: string
): Promise<T> {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return promise
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new TimeoutError(label, timeoutMs))
    }, timeoutMs)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err: unknown) => {
        clearTimeout(timer)
        reject(err)
      }
    )
  })
}
